import { PermissionFeature, PermissionFeatureToActions } from "@/protocols/permission"

export type PermissionSubgroupConfig<Feature extends PermissionFeature = PermissionFeature> = {
	id: string
	label: string
	feature: Feature
	actions: PermissionFeatureToActions[Feature][]
}

export type PermissionMacroGroupConfig = {
	id: string
	label: string
	subgroups: PermissionSubgroupConfig[]
}

export const PERMISSION_MACRO_GROUPS: PermissionMacroGroupConfig[] = [
	{
		id: "chats",
		label: "Chats",
		subgroups: [
			{
				id: "filas",
				label: "Filas",
				feature: "chat",
				actions: ["view-chat-bot-queue", "view-message-blast-queue", "view-integrations-queue", "view-archived-queue"]
			},
			{
				id: "atendimentos",
				label: "Atendimentos",
				feature: "chat",
				actions: ["view-other-attendant-attendances", "finish-others-attendance", "transfer-any-chat"]
			},
			{
				id: "filtros",
				label: "Filtros",
				feature: "chat",
				actions: ["filter-by-team", "filter-by-attendant"]
			},
			{
				id: "conversas",
				label: "Conversas",
				feature: "chat",
				actions: ["delete-chat", "delete-message", "start-attendance"]
			},
			{
				id: "soneca",
				label: "Soneca",
				feature: "snooze",
				actions: ["manage"]
			}
		]
	},
	{
		id: "contatos",
		label: "Contatos",
		subgroups: [
			{
				id: "notas-de-contato",
				label: "Notas de Contato",
				feature: "contact-note",
				actions: ["create", "edit-others", "delete-others"]
			},
			{
				id: "contatos-dados",
				label: "Dados de Contatos",
				feature: "contact",
				actions: ["create", "update", "disable", "block-unblock-waba", "export", "send-to-bot", "view-sensitive-data"]
			}
		]
	},
	{
		id: "usuarios-e-acessos",
		label: "Usuários e Acessos",
		subgroups: [
			{
				id: "atendentes",
				label: "Atendentes",
				feature: "attendant",
				actions: ["delete", "update", "invite", "block"]
			},
			{
				id: "cargos",
				label: "Cargos",
				feature: "role",
				actions: ["create", "update", "delete"]
			},
			{
				id: "equipes",
				label: "Equipes",
				feature: "team",
				actions: ["create", "update", "delete"]
			}
		]
	},
	{
		id: "configuracoes",
		label: "Configurações",
		subgroups: [
			{
				id: "instancia",
				label: "Instância",
				feature: "instance",
				actions: ["update"]
			},
			{
				id: "painel-do-gestor",
				label: "Painel do Gestor",
				feature: "manager-panel",
				actions: ["access", "subscriptions-access"]
			},
			{
				id: "canal",
				label: "Canal",
				feature: "channel",
				actions: ["edit", "disconnect", "connect"]
			},
			{
				id: "plugin",
				label: "Plugin",
				feature: "plugin",
				actions: ["disconnect", "connect"]
			}
		]
	},
	{
		id: "recursos",
		label: "Recursos",
		subgroups: [
			{
				id: "campos-personalizados",
				label: "Campos Personalizados",
				feature: "custom-field",
				actions: ["create", "update", "delete"]
			},
			{
				id: "respostas-rapidas",
				label: "Respostas Rápidas",
				feature: "quick-reply",
				actions: ["create", "update", "delete"]
			},
			{
				id: "tag",
				label: "Tag",
				feature: "tag",
				actions: ["create", "update", "delete"]
			},
			{
				id: "link-rastreavel",
				label: "Link Rastreável",
				feature: "trackable-link",
				actions: ["create", "update"]
			},
			{
				id: "template-waba",
				label: "Template WABA",
				feature: "waba-template",
				actions: ["create", "update", "delete"]
			},
			{
				id: "admin-console-acesso",
				label: "Admin Console",
				feature: "admin-console",
				actions: ["access"]
			}
		]
	},
	{
		id: "automacoes",
		label: "Automações",
		subgroups: [
			{
				id: "envio-em-massa",
				label: "Envio em Massa",
				feature: "message-blast",
				actions: ["create", "update", "delete", "cancel-scheduled", "schedule", "fire"]
			},
			{
				id: "bot",
				label: "Bot",
				feature: "chat-bot",
				actions: ["create", "update", "delete", "duplicate", "export"]
			},
			{
				id: "integracao",
				label: "Integração",
				feature: "integration",
				actions: ["create", "update", "delete"]
			}
		]
	},
	{
		id: "metricas-de-vendas",
		label: "Métricas de Vendas",
		subgroups: [
			{
				id: "metricas-de-vendas",
				label: "Métricas de Vendas",
				feature: "board-metrics",
				actions: ["access", "edit-config"]
			}
		]
	},
	{
		id: "perfil",
		label: "Perfil",
		subgroups: [
			{
				id: "minha-conta",
				label: "Minha Conta",
				feature: "profile",
				actions: ["update"]
			}
		]
	}
]

export const getFilteredPermissionMacroGroups = (channelType: string | undefined): PermissionMacroGroupConfig[] => {
	const isWhatsappChannel = channelType === "whatsapp"
	const isWabaChannel = channelType === "waba"

	const subgroupIdToVisibilityFilter: Record<string, () => boolean> = {
		integracao: () => isWhatsappChannel,
		"template-waba": () => isWabaChannel
	}

	const subgroupIdToActionsFilter: Record<string, (subgroup: PermissionSubgroupConfig) => PermissionSubgroupConfig> = {
		"contatos-dados": (subgroup) => {
			if (!isWabaChannel) {
				return {
					...subgroup,
					actions: subgroup.actions.filter(action => action !== "block-unblock-waba")
				}
			}
			return subgroup
		},
		filas: (subgroup) => {
			if (!isWhatsappChannel) {
				return {
					...subgroup,
					actions: subgroup.actions.filter(action => action !== "view-integrations-queue")
				}
			}
			return subgroup
		}
	}

	return PERMISSION_MACRO_GROUPS.map(macroGroup => ({
		...macroGroup,
		subgroups: macroGroup.subgroups
			.filter(subgroup => {
				const visibilityFilter = subgroupIdToVisibilityFilter[subgroup.id]
				return visibilityFilter ? visibilityFilter() : true
			})
			.map(subgroup => {
				const actionsFilter = subgroupIdToActionsFilter[subgroup.id]
				return actionsFilter ? actionsFilter(subgroup) : subgroup
			})
	}))
}
